<template>
    <div class="tw-border tw-border-solid tw-border-gray-200 tw-rounded tw-overflow-hidden tw-mb-4">
        <!-- Collapsible header -->
        <div @click="isOpen = !isOpen" class="tw-bg-gray-50 tw-px-4 tw-py-2 tw-cursor-pointer tw-flex tw-items-center tw-justify-between">
            <h2 class="tw-text-sm tw-font-semibold tw-text-gray-700">{{ $t('common.cross-products') }}</h2>
            <icon :icon="isOpen ? 'heroicons-outline:chevron-up' : 'heroicons-outline:chevron-down'" class="tw-text-gray-500 tw-text-lg" />
        </div>
        
        <!-- Table content (shown when expanded) -->
        <div v-if="isOpen" class="tw-overflow-auto">
            <table class="tw-min-w-full tw-leading-normal">
                <thead>
                    <tr>
                        <th class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 tw-bg-gray-50 tw-text-start tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider">
                            {{ $t('fields.product') }}
                        </th>
                        <th class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 tw-bg-gray-50 tw-text-center tw-text-xs tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-w-[80px]">
                            {{ $t('fields.price') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <!-- Loading state -->
                    <tr v-if="loading" class="tw-border-b tw-border-solid tw-border-gray-100">
                        <td colspan="2" class="tw-px-3 tw-py-4">
                            <div class="tw-flex tw-items-center tw-justify-center tw-gap-2">
                                <icon icon="line-md:loading-twotone-loop" class="tw-text-primary-500 tw-text-xl" />
                                <span class="tw-text-sm tw-text-gray-500">{{ $t('common.loading') }}</span>
                            </div>
                        </td>
                    </tr>
                    
                    <!-- Products list -->
                    <tr v-else v-for="product in crossProducts" :key="product.id" class="tw-border-b tw-border-solid tw-border-gray-100 last:tw-border-b-0 tw-group even:tw-bg-gray-50/50 hover:tw-bg-primary-50/60 tw-duration-100">
                        <td class="tw-px-3 tw-py-2 tw-text-sm">
                            <div class="tw-flex tw-flex-col">
                                <p class="tw-truncate tw-max-w-[200px] tw-font-medium">{{ product.name }}</p>
                                <p v-if="product.note" class="tw-text-xs tw-text-gray-500 tw-truncate tw-max-w-[200px]">{{ product.note }}</p>
                            </div>
                        </td>
                        <td class="tw-px-3 tw-py-2 tw-text-sm tw-text-center">
                            <p class="tw-font-semibold tw-flex tw-items-center tw-justify-center tw-gap-1">
                                <span>{{ product.price }}</span>
                                <span class="tw-text-xs tw-text-gray-500">{{ $t('currencies.' + CURRENCY) }}</span>
                            </p>
                        </td>
                    </tr>
                    
                    <!-- No results state -->
                    <tr v-if="!loading && crossProducts.length === 0" class="tw-border-b tw-border-solid tw-border-gray-100">
                        <td colspan="2" class="tw-px-3 tw-py-4 tw-text-sm tw-text-center tw-text-gray-500">
                            {{ $t('messages.table-no-results') }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script setup>
import { inject, ref, onMounted, watch } from 'vue';
import { cross } from '@/api/products';
import { CURRENCY } from '@/config/app';

const order = inject('order');
const isOpen = ref(true);
const loading = ref(false);
const crossProducts = ref([]);

// Mock function to simulate API call - replace with actual API call later
const fetchCrossProducts = async (productIds) => {
    console.log('Product IDs:', productIds);
    console.log('Order:', order);
    
    // Don't attempt to fetch if there are no product IDs
    if (!productIds || productIds.length === 0 || productIds.every(id => !id)) {
        crossProducts.value = [];
        return;
    }
    
    loading.value = true;
    
    cross(productIds)
    .then(response => {
        console.log(response);
        if (response.data && response.data.code === 'SUCCESS') {
            // Map the response to our component data format
            if (response.data.cross_products && response.data.cross_products.length > 0) {
                crossProducts.value = response.data.cross_products.map(item => ({
                    id: item.id,
                    name: item.cross_product_name || 'Unknown Product',
                    price: item.price || '0.00',
                    note: item.note || '',
                    product_id: item.product_id,
                    cross_product_id: item.cross_product_id
                }));
            } else {
                // Empty cross products array
                crossProducts.value = [];
            }
        } else {
            // If response format is unexpected
            crossProducts.value = [];
        }
    })
    .catch(error => {
        console.error('Error fetching cross products:', error);
        crossProducts.value = [];
    })
    .finally(() => {
        loading.value = false;
    });
};

// Get product IDs from order items
const getProductIdsFromOrder = () => {
    if (!order.value || !order.value.items || !order.value.items.length) return [];
    
    // Extract product_id from each order item and filter out any undefined or null values
    const productIds = order.value.items
        .map(item => item.product_id)
        .filter(id => id);
    
    // Get unique product IDs
    return [...new Set(productIds)];
};

// Initial fetch on component mount
onMounted(() => {
    const productIds = getProductIdsFromOrder();
    console.log('Product IDs:', productIds);
    fetchCrossProducts(productIds);
});

// Watch for changes in order items to refresh data
watch(() => order.value?.items, (newItems) => {
    if (newItems) {
        const productIds = getProductIdsFromOrder();
        fetchCrossProducts(productIds);
    }
}, { deep: true });
</script>

<style scoped>
/* Add any additional custom styles here if needed */
</style>
